import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { DateOptionSelectOption, DefaultPastDateRangeOptions, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { IssuePriority, IssuePriorityFilter, IssuePriorityFilterOptions, IssueResponse, IssueStatus, isValidIssuePriorityFilter } from 'Models/Issues';
import { OrganizationResponseBase } from 'Models/Organization';
import { countByMonth, countClosedIssuesByMonth, countTotalIssuesByMonth, getClosedIssuesPerMonth, getMonths, getProductModuleForDashboardType } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import styles from './DashboardsHistoricalIssues.module.css';
import { HistoricalIssuesDisplayModal, IssueModalDisplayType } from './HistoricalIssuesDisplayModal/HistoricalIssuesDisplayModal';

export interface DashboardsHistoricalIssuesProps {
    allIssues?: IssueResponse[];
    dashboardType: IssuesExceptionsDashboardType;
    issuesError?: string;
    organizations?: OrganizationResponseBase[];
    organizationsError?: string;
}

enum Modals {
    NONE,
    DISPLAY_ISSUES,
    CUSTOM_DATE,
}

/**
 * Renders a bar-and-line chart that displays, over a given time period, and grouped by month:
 * * The number of issues opened
 * * The number of issues closed
 * * The total number of issues in the system
 *
 * TODO: rename this. It's not just for closed issues.
 */
export const DashboardsHistoricalIssues = (props: DashboardsHistoricalIssuesProps): JSX.Element => {
    const dashboardIssues = useMemo(() => {
        if (!props.allIssues) return undefined;
        const moduleType = getProductModuleForDashboardType(props.dashboardType);
        return props.allIssues.filter((issue) => issue.type === moduleType && issue.status !== IssueStatus.DRAFT_OPEN);
    }, [props.allIssues, props.dashboardType]);

    const [closedIssues, setClosedIssues] = useState<number[]>();
    const [totalOpenIssues, setTotalOpenIssues] = useState<number[]>();
    const [openedIssues, setOpenedIssues] = useState<number[]>();
    const [monthLabel, setMonthLabel] = useState<string[]>();
    const [clickedIssues, setClickedIssue] = useState<IssueResponse[]>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [clickedDate, setClickedDate] = useState<string>();
    const [modalDisplayType, setModalDisplayType] = useState<IssueModalDisplayType>();
    const [issueFilterType, setIssueFilterType] = useState<IssuePriorityFilter>(IssuePriorityFilter.ALL);
    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);

    useEffect(() => {
        const issuesPriorityFilter = (issue: IssueResponse): boolean => {
            if (issueFilterType === IssuePriorityFilter.ALL) {
                return true;
            } else if (issueFilterType === IssuePriorityFilter.HIGH) {
                return issue.priority === IssuePriority.HIGH;
            } else if (issueFilterType === IssuePriorityFilter.MEDIUM) {
                return issue.priority === IssuePriority.MEDIUM;
            } else if (issueFilterType === IssuePriorityFilter.LOW) {
                return issue.priority === IssuePriority.LOW;
            } else {
                return true;
            }
        };
        if (dashboardIssues) {
            const dashboardIssuesByPriority = dashboardIssues.filter(issuesPriorityFilter);
            const allClosedIssues = dashboardIssuesByPriority.filter((issue) => issue.status === IssueStatus.CLOSED);

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const openedIssues = countByMonth(dashboardIssuesByPriority, months);
            const issuesClosedByMonth = countClosedIssuesByMonth(allClosedIssues, months);
            const totalIssues = countTotalIssuesByMonth(dashboardIssuesByPriority, months);

            setClosedIssues(issuesClosedByMonth);
            setOpenedIssues(openedIssues);
            setMonthLabel(months);
            setTotalOpenIssues(totalIssues);
        }
    }, [dashboardIssues, issueFilterType, dateFilterHookValues]);

    const handleChartClick = (item: string) => {
        const currentFilteredIssues = dashboardIssues?.filter((issue) => {
            if (issueFilterType === IssuePriorityFilter.ALL) {
                return true;
            } else if (issueFilterType === IssuePriorityFilter.HIGH) {
                return issue.priority === IssuePriority.HIGH;
            } else if (issueFilterType === IssuePriorityFilter.MEDIUM) {
                return issue.priority === IssuePriority.MEDIUM;
            } else if (issueFilterType === IssuePriorityFilter.LOW) {
                return issue.priority === IssuePriority.LOW;
            } else {
                return true;
            }
        });

        const clickedItem = item.split(' | ');
        const clickedDate = toTitleCase(clickedItem[0]);
        if (clickedItem[1] === 'Total Closed' && currentFilteredIssues) {
            const allClosedIssues = currentFilteredIssues.filter((issue) => issue.status === IssueStatus.CLOSED);
            setClickedIssue(getClosedIssuesPerMonth(allClosedIssues, clickedDate));
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_ISSUES);
            setModalDisplayType(IssueModalDisplayType.CLOSED);
        } else if (clickedItem[1] === 'Total Opened' && currentFilteredIssues) {
            const issues = currentFilteredIssues.filter((issue) => moment(issue.created_timestamp).format('MMM YY') === clickedDate);
            setClickedIssue(issues);
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_ISSUES);
            setModalDisplayType(IssueModalDisplayType.OPEN);
        }
    };

    const handleRiskTypeChange = (value: ChangeEventType, formFieldId: string) => {
        if (isValidIssuePriorityFilter(value)) {
            setIssueFilterType(value as IssuePriorityFilter);
        }
    };

    if (props.issuesError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Issue History
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.issuesError}
                </Text>
            </>
        );
    }

    if (props.organizationsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Issue History
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.organizationsError}
                </Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_ISSUES && clickedDate && clickedIssues && modalDisplayType !== undefined && <HistoricalIssuesDisplayModal selectedIssue={clickedIssues} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} modalType={modalDisplayType} dashboardType={props.dashboardType} organizations={props.organizations} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Issue History
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="issuePriority" formFieldLabel="Priority..." options={IssuePriorityFilterOptions} selectedOption={issueFilterType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultPastDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {closedIssues && monthLabel && totalOpenIssues && openedIssues ? (
                <GroupedBarsChart
                    onChartClick={handleChartClick}
                    xAxisLabels={monthLabel}
                    line={{ name: 'Total Issues', color: CSSColors.YELLOW, data: totalOpenIssues }}
                    barsSeriesArray={[
                        { name: 'Total Opened', color: CSSColors.LIGHT_BLUE, data: openedIssues },
                        { name: 'Total Closed', color: CSSColors.GRAY, data: closedIssues },
                    ]}
                    yAxisInfo={[
                        { name: 'Total Closed/Opened', position: 'left', minInterval: 1 },
                        { name: 'Total Issues', position: 'right', minInterval: 1 },
                    ]}
                    groupingMode="clustered"
                />
            ) : (
                <Skeleton variant="rounded" width={'100%'} height={300} />
            )}
        </>
    );
};
